/**
 * StageTransitionControl Component
 *
 * Controls for advancing discussion stages with permission checks.
 *
 * Story FOS-4.2.3: Leaf Components Batch 1 (Stage/Quality)
 *
 * Features:
 * - Only visible to Proposer/Contributors
 * - Validates quality gates before allowing transition
 * - Confirmation dialog before stage change
 *
 * @module components/governance/StageTransitionControl
 * @see AC-4.1.5.7 - Stage transitions only available to Proposer/Contributors with valid permissions
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, AlertCircle, Check, X } from 'lucide-react';
import type { DiscussionStage } from '../../stores/discussionStore';

/**
 * Props for StageTransitionControl component
 */
export interface StageTransitionControlProps {
  /** Current discussion stage */
  currentStage: DiscussionStage;
  /** Whether current user is proposer or contributor */
  canTransition: boolean;
  /** Whether all quality gates are met */
  gatesMet: boolean;
  /** Unmet gate descriptions (for error message) */
  unmetGates?: string[];
  /** Callback when stage transition is requested */
  onTransition: () => Promise<void>;
  /** Whether the control is disabled (e.g., feature not yet available) */
  disabled?: boolean;
  /** Tooltip text when disabled */
  disabledTooltip?: string;
  /** Optional class name */
  className?: string;
}

/**
 * Next stage mapping
 */
const NEXT_STAGE: Record<DiscussionStage, DiscussionStage | null> = {
  Brainstorm: 'Refining',
  Refining: 'Ready',
  Ready: null,
};

/**
 * Stage transition labels
 */
const TRANSITION_LABELS: Record<DiscussionStage, string> = {
  Brainstorm: 'Advance to Refining',
  Refining: 'Mark as Ready',
  Ready: '',
};

/**
 * Confirmation dialog component
 *
 * Accessibility features:
 * - Escape key closes dialog
 * - Focus trapped within dialog
 * - Auto-focus on cancel button
 * - Renders via portal to avoid z-index issues
 */
function ConfirmationDialog({
  currentStage,
  onConfirm,
  onCancel,
  isLoading,
}: {
  currentStage: DiscussionStage;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const nextStage = NEXT_STAGE[currentStage];
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  // Handle Escape key and focus trapping
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) {
        onCancel();
      }

      // Focus trap: Tab cycles within dialog
      if (e.key === 'Tab' && dialogRef.current) {
        const focusableElements = dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Auto-focus cancel button on mount
    cancelButtonRef.current?.focus();

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel, isLoading]);

  const dialogContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      data-testid="confirmation-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
      onClick={(e) => {
        // Close on backdrop click
        if (e.target === e.currentTarget && !isLoading) {
          onCancel();
        }
      }}
    >
      <div
        ref={dialogRef}
        className="bg-card rounded-lg shadow-lg p-6 max-w-md w-full mx-4 border border-border"
      >
        <h3 id="dialog-title" className="text-lg font-semibold text-foreground mb-2">
          Confirm Stage Transition
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Are you sure you want to advance this discussion from{' '}
          <span className="font-medium text-foreground">{currentStage}</span> to{' '}
          <span className="font-medium text-foreground">{nextStage}</span>?
        </p>
        {currentStage === 'Refining' && (
          <div className="p-3 rounded-md bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 mb-4">
            <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
              <Check className="h-4 w-4" />
              <span className="text-sm font-medium">
                This discussion will be ready for proposal extraction.
              </span>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            ref={cancelButtonRef}
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted"
            data-testid="cancel-transition"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
            data-testid="confirm-transition"
          >
            {isLoading ? (
              <>
                <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <ArrowRight className="h-4 w-4" />
                Confirm
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  // Render via portal to document.body to avoid z-index stacking issues
  return createPortal(dialogContent, document.body);
}

/**
 * StageTransitionControl - Controls for advancing discussion stages
 *
 * @example
 * ```tsx
 * <StageTransitionControl
 *   currentStage="Brainstorm"
 *   canTransition={isProposer || isContributor}
 *   gatesMet={allGatesMet}
 *   unmetGates={['Need 1 more participant', 'Need 3 more comments']}
 *   onTransition={() => advanceStage(discussionId)}
 * />
 * ```
 */
export function StageTransitionControl({
  currentStage,
  canTransition,
  gatesMet,
  unmetGates = [],
  onTransition,
  disabled = false,
  disabledTooltip,
  className = '',
}: StageTransitionControlProps) {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nextStage = NEXT_STAGE[currentStage];

  const handleClick = useCallback(() => {
    if (gatesMet) {
      setShowConfirmation(true);
    }
  }, [gatesMet]);

  const handleConfirm = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      await onTransition();
      setShowConfirmation(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to advance stage');
    } finally {
      setIsLoading(false);
    }
  }, [onTransition]);

  const handleCancel = useCallback(() => {
    setShowConfirmation(false);
  }, []);

  // Don't render if no next stage or user can't transition
  if (!nextStage || !canTransition) {
    return null;
  }

  return (
    <div className={`space-y-2 ${className}`} data-testid="stage-transition-control">
      {/* Main button */}
      <div className="relative" title={disabled ? disabledTooltip : undefined}>
        <button
          onClick={handleClick}
          disabled={disabled || !gatesMet || isLoading}
          className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md font-medium transition-colors ${
            disabled
              ? 'bg-muted text-muted-foreground cursor-not-allowed opacity-60'
              : gatesMet
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : 'bg-muted text-muted-foreground cursor-not-allowed'
          }`}
          data-testid="transition-button"
        >
          <ArrowRight className="h-4 w-4" />
          {TRANSITION_LABELS[currentStage]}
        </button>
      </div>

      {/* Gates not met warning */}
      {!gatesMet && unmetGates.length > 0 && (
        <div
          className="p-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800"
          data-testid="unmet-gates-warning"
        >
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-1">
                Quality gates not met
              </p>
              <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-1">
                {unmetGates.map((gate, index) => (
                  <li key={index}>• {gate}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 p-2 rounded-md bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 text-sm">
          <X className="h-4 w-4" />
          {error}
        </div>
      )}

      {/* Confirmation dialog */}
      {showConfirmation && (
        <ConfirmationDialog
          currentStage={currentStage}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}

export default StageTransitionControl;
